import {
  CSSProperties,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  Editor,
  throttle,
  TLPageId,
  TLShapeId,
  useEditor,
  useValue,
} from "tldraw"
import { selectedControlColor } from "./Toolbar"
import { useAppDispatch } from "../app/hooks"
import { showPageSettingsDialog } from "../features/app/appSlice"
import { LDPage } from "../../shared/LDPage"
import { CustomDrawShapeProps } from "../../shared/CustomDrawShape"
import { styled } from "@mui/material"
import { LDShape } from "../../shared/LDShape"
import { Expr } from "../../shared/Expr"
import { flattenParams } from "../shapes/flattenParams"

const previewImageStyles: CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
}

type PageThumbProps = {
  pageId: TLPageId
  selected?: boolean
  onSelect: (pageId: TLPageId) => void
  onStickerDrag: (
    pageId: TLPageId,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void
}
export function PageThumb({
  pageId,
  onSelect,
  selected = false,
  onStickerDrag,
}: PageThumbProps) {
  // After dragging, we want to yield control to the sticker being dragged
  // so we only want to call onStickerDrag once:
  // - desktop: onDragStart
  // - mobile: onTouchMove (which is called multiple times as you drag)
  //
  // but on mobile we want to short circuit the onTouchMove after the first call
  // and only call onStickerDrag once and have the sticker takes over dragging at that point
  // so we use dragCounter (state) to create a new key and force a re-mount
  // which stops the onTouchMove from being called multiple times
  // but that is not fast enough to prevent the onTouchMove from being called a few times at the beginning
  // so we also use a ref boolean to stop onTouchMove after the first call
  //
  // then we use an effect to reset the ref boolean after the dragCounter changes
  const [dragCounter, setDragCounter] = useState(0)
  const isDragging = useRef(false)
  useEffect(() => {
    isDragging.current = false
  }, [dragCounter])
  // ----------------------------

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewDimensions, setPreviewDimensions] = useState<{
    w: number
    h: number
  } | null>(null)
  const editor = useEditor()

  // const { addDialog } = useDialogs()

  const dispatch = useAppDispatch()

  const shapeIds = useValue("shapesIds", () => editor.getPageShapeIds(pageId), [
    editor,
  ])
  const pageMeta = useValue(
    "pageMeta",
    () => {
      const page = editor.getPage(pageId)! as LDPage
      return page.meta
    },
    [editor],
  )

  // collect all the shapes that are not in the process of being drawn
  const completeShapeIds = useValue(
    "shapes",
    () => {
      const _completeShapeIds: TLShapeId[] = []
      for (const id of shapeIds) {
        const s = editor.getShape(id) as LDShape | undefined
        const params = flattenParams(s, editor)

        if (s) {
          const shapePage = editor.getPage(
            LDShape.getSourcePageId(editor, s),
          )! as LDPage
          const layer = shapePage.meta.layers[LDShape.getLayerId(s)]
          if (Expr.evaluate(layer?.visible ?? Expr.lit(true), params)) {
            if (
              s.type === "custom-draw" &&
              (s.props as CustomDrawShapeProps).isComplete
            ) {
              _completeShapeIds.push(id)
            }
            if (s.type === "sticker") {
              _completeShapeIds.push(id)
            }
            if (s.type === "text2") {
              _completeShapeIds.push(id)
            }
            if (s.type === "custom-geo") {
              _completeShapeIds.push(id)
            }
            // SUPPORTED YET
            if (s.type === "custom-line") {
              _completeShapeIds.push(id)
            }
            if (s.type === "arrow") {
              _completeShapeIds.push(id)
            }
          }
        }
        //x}
      }
      return _completeShapeIds
    },
    [editor, shapeIds],
  )

  const blobUrlRef: MutableRefObject<string | null> = useRef(null)

  const updatePreview = useCallback(
    throttle(async (editor: Editor, shapeIds: TLShapeId[]) => {
      // if ids are empty, tldraw exports all the shapes, which is not what we want
      // so we bail if that's the case
      if (shapeIds.length === 0) {
        setPreviewUrl(null)
        return
      }

      const { blob, width, height } = await editor.toImage(shapeIds)

      setPreviewDimensions({ w: width, h: height })

      const blobUrl = URL.createObjectURL(blob)
      setPreviewUrl(blobUrl)
      // clear the previous blob url
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
      blobUrlRef.current = blobUrl
    }, 500),
    [],
  )

  useEffect(() => {
    updatePreview(editor, completeShapeIds)
  }, [completeShapeIds, pageMeta, editor])

  const handlePageThumbClick = useCallback(() => {
    // if the page is already the selected one
    // show the page settings
    if (selected) {
      // addDialog({
      //   component: PageSettingsDialog,
      //   onClose: () => {

      //   },
      // })
      dispatch(showPageSettingsDialog())
    } else {
      onSelect(pageId)
    }
  }, [onSelect, pageId, selected])

  return (
    <PageThumbDiv
      key={dragCounter}
      onClick={handlePageThumbClick}
      selected={selected}
      draggable
      onTouchMove={(event: React.TouchEvent<HTMLDivElement>) => {
        if (isDragging.current) return
        isDragging.current = true
        setDragCounter((v) => v + 1)
        event.preventDefault()
        const target = event.target as HTMLDivElement // or HTMLImageElement, depending on your element type
        const rect = target.getBoundingClientRect()

        const x = rect.left
        const y = rect.top - 55

        // must only be called once
        // after that, the sticker takes over
        previewDimensions &&
          onStickerDrag(pageId, x, y, previewDimensions.w, previewDimensions.h) // Pass the element's coordinates
      }}
      onDragStart={(event: any) => {
        event.preventDefault()
        const target = event.target as HTMLDivElement // or HTMLImageElement, depending on your element type
        const rect = target.getBoundingClientRect()

        const x = rect.left
        const y = rect.top - 55

        // must only be called once
        // after that, the sticker takes over
        previewDimensions &&
          onStickerDrag(pageId, x, y, previewDimensions.w, previewDimensions.h) // Pass the element's coordinates
      }}
    >
      {previewUrl && (
        <img
          style={previewImageStyles}
          title={pageId}
          draggable={false}
          src={previewUrl}
        />
      )}
    </PageThumbDiv>
  )
}

type PageThumbDivProps = {
  selected?: boolean
}

const PageThumbDiv = styled("div")<PageThumbDivProps>({
  display: "flex",
  outline: "1px solid #ddd",
  cursor: "pointer",
  height: 64,
  width: 64,
  touchAction: "none",
  variants: [
    {
      props: { selected: true },
      style: {
        outline: `3px solid ${selectedControlColor}`,
      },
    },
  ],
})
